import React, { useEffect, useState } from "react";
import {
  reduxForm,
  FormSection,
  getFormValues,
  getFormSyncErrors
} from "redux-form";
import AddressSellCheckout from "@components/forms/AddressSellCheckout";
import SellContactInformation from "./SellContactInformation";
import SecurityImages from "./SecurityImages";
import SellCardsDetails from "./SellCardsDetails";
import SellOrderSummary from "./SellOrderSummary";
import { defaultAction } from "@actions/defaultAction";
import { postPrimaryAddress } from "@actions/customerInfo";
import { DISPLAY_MAIN_PAYMENT_ERROR } from "@constants/actionTypes";
import loadable from "@loadable/component";
const SellVerificationCC = loadable(() =>
  import(
    /* webpackChunkName: "SellVerificationCC" */ "./forms/SellVerificationCC"
  )
);
import MainContentSellContainer from "./MainContentSellContainer";
import SellFormDesktopBottomButton from "./SellFormDesktopBottomButton";
import { verifyCC } from "@utils/validators/verifyCC";
import { useDispatch, useSelector } from "react-redux";

const PayPalSellWorkflow = ({
  handleSubmit,
  submitFailed,
  dispatch,
  valid,
  change
}) => {
  const regularReduxDispatch = useDispatch();
  const payPalSell = useSelector(state => getFormValues("payPalSell")(state));
  const payPalSellErrors = useSelector(state =>
    getFormSyncErrors("payPalSell")(state)
  );
  const customerGetStatus = useSelector(
    state => state.customerInfo.customerGetStatus
  );
  const missingInfo = useSelector(state => state.customerInfo.missingInfo);
  const mainPaymentError = useSelector(
    state => state.customerInfo.mainPaymentError
  );
  const cardKnoxState = useSelector(state => state.cardKnox);
  const phone = useSelector(state => state.customerInfo.phone);
  const [displayPrimary, setDisplayPrimary] = useState(null);
  const [phoneCompleted, changePhoneCompleted] = useState(!!phone);
  const [openPrimaryForm, changeOpenPrimaryForm] = useState(false);
  const [openVerificationForm, changeOpenVerificationForm] = useState(false);
  const [openToEdit, changeOpenToEdit] = useState(false);

  useEffect(() => {
    if (customerGetStatus === "success") {
      if (!missingInfo) {
        setDisplayPrimary(false);
      } else {
        setDisplayPrimary(true);
      }
    }
  }, [customerGetStatus]);

  useEffect(() => {
    if (!missingInfo) {
      changeOpenToEdit(true);
    }
  }, [missingInfo]);

  useEffect(() => {
    if (submitFailed && !mainPaymentError) {
      window.scrollTo(0, 0);
      regularReduxDispatch(
        defaultAction(
          DISPLAY_MAIN_PAYMENT_ERROR,
          "Please fix the errors and try again"
        )
      );
    }
  }, [submitFailed]);

  useEffect(() => {
    if (phoneCompleted) {
      if (missingInfo) {
        changeOpenPrimaryForm(true);
      }
    }
  }, [phoneCompleted]);

  useEffect(() => {
    if (openPrimaryForm) {
      if (openVerificationForm && missingInfo) {
        changeOpenVerificationForm(false);
      }
    }

    if (!openPrimaryForm) {
      if (!missingInfo) {
        changeOpenVerificationForm(true);
      }
    }
  }, [openPrimaryForm]);

  useEffect(() => {
    if (openVerificationForm) {
      if (openPrimaryForm) {
        changeOpenPrimaryForm(false);
      }
    }
  }, [openVerificationForm]);

  const primaryContinueAction = async () => {
    const response = await regularReduxDispatch(
      postPrimaryAddress(payPalSell.primaryResidence)
    );
    return response;
  };

  return (
    <form onSubmit={handleSubmit}>
      {payPalSell && Object.keys(payPalSell).length > 0 && (
        <MainContentSellContainer>
          <div>
            <SellCardsDetails />
            <SellContactInformation
              errors={payPalSellErrors}
              changePhoneCompleted={changePhoneCompleted}
              phoneValue={payPalSell?.newPhone}
              primaryResidence={
                displayPrimary ? null : payPalSell.primaryResidence
              }
            />
            {displayPrimary && (
              <FormSection name="primaryResidence">
                <AddressSellCheckout
                  heading="Primary Residence"
                  phoneCompleted={phoneCompleted}
                  errors={payPalSellErrors.primaryResidence}
                  addressLineTwo={payPalSell?.primaryResidence?.street2}
                  formContent={payPalSell?.primaryResidence}
                  openForm={openPrimaryForm}
                  changeOpenForm={changeOpenPrimaryForm}
                  flowNumber={"2"}
                  openToEdit={openToEdit}
                  continueAction={primaryContinueAction}
                  displayPrimary={displayPrimary}
                />
              </FormSection>
            )}
            <FormSection name="verification">
              <SellVerificationCC
                openForm={openVerificationForm}
                heading={"Identity Verification"}
                errors={payPalSellErrors.verification}
                formContent={payPalSell?.verification}
                openToEdit={openToEdit}
                missingInfo={missingInfo}
                changeOpenForm={changeOpenVerificationForm}
                flowNumber={displayPrimary ? "3" : "2"}
                checkBoxText={
                  "The address associated with this credit card is the same as my primary address"
                }
                checkBoxName={"sameAsPrimary"}
                addressLineTwo={payPalSell?.primaryResidence?.street2}
                changeReduxField={change}
              />
            </FormSection>
            <SellFormDesktopBottomButton
              reduxFormDispatch={dispatch}
              valid={valid}
              paypalButtonClass={"paypal-sell-btn-bottom"}
              missingInfo={
                missingInfo ||
                !verifyCC(payPalSell?.verification, cardKnoxState)
              }
            />
          </div>
          <div>
            <SellOrderSummary
              reduxFormDispatch={dispatch}
              valid={valid}
              missingInfo={
                missingInfo ||
                !verifyCC(payPalSell?.verification, cardKnoxState)
              }
            />
            <SecurityImages />
          </div>
        </MainContentSellContainer>
      )}
    </form>
  );
};

export default reduxForm({
  form: "payPalSell",
  onSubmit: f => f //noop to allow submit from PayPal callback
})(PayPalSellWorkflow);
